import { FormControl, NgForm, NgModel } from '@angular/forms';
import { Component, Inject, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { TranslationService } from '@sdbn/common';
import { DialogService, ResponseMessage } from '@sdbn/appclient';
import { DialogLayout } from 'src/app/shared/dialog-layout/dialog-layout';
import { TruckSupplementaryEquipmentComponent } from '../supplementary-equipment/truck-supplementary-equipment.component';
import { ResponseStatus } from '@sdbn/appclient';
import { TmcContextService } from 'src/tmc-common/services/tmc-context.service';
import { MessagingProvider } from 'src/tmc-yard/client/model/messagingprovider';
import { TruckDetails } from 'src/tmc-yard/widgets/control/truck/truck.model';
import { TruckService } from '../../truck.service';
import { debounceTime, switchMap } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { ConfigurationService } from '@sdbn/appclient';
import { TMCClientParameters } from 'src/tmc-common/tmc-client-parameters';
import { MatCheckboxChange } from '@angular/material/checkbox';

@Component({
  selector: 'app-truck-details-dialog',
  templateUrl: './truck-details-dialog.component.html',
  styleUrls: ['./truck-details-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TruckDetailsDialogComponent implements OnInit {

  numberPlateControl = new FormControl();
  autocomplateTrucks = new Array<TruckDetails>();
  autocompleteSubscription: Subscription;
  responseMessages: ResponseMessage[];

  serviceVehiclesEnabled = false;

  /** The application model for this dialog. */
  truck: TruckDetails = new TruckDetails();
  origTruck: TruckDetails = null;

  useGlobalConnectEditable = true;

  isLoading = false;

  readonly = false;

  clientParameters = TMCClientParameters;

  @ViewChild('truckForm') truckForm: NgForm;
  @ViewChild('truckSupplementaryEquipmentComponent') truckSupplementaryEquipmentComponent: TruckSupplementaryEquipmentComponent;

  constructor(
    private dialogService: DialogService,
    public dialogRef: MatDialogRef<TruckDetailsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private data: any,
    private truckService: TruckService,
    public cps: ConfigurationService,
    public translationService: TranslationService,
    private tmcContextService: TmcContextService) {
    this.truck = new TruckDetails(data.model);
    this.origTruck = new TruckDetails(data.model);
    this.useGlobalConnectEditable = this.truck.useGlobalConnect == null || this.truck.useGlobalConnect === false;
    this.dialogRef.addPanelClass(DialogLayout.panelClass);

    if (data.readonly != null && typeof data.readonly === 'boolean') {
      this.readonly = data.readonly;
    }

    if (this.cps.isEnabled(this.clientParameters.FEATURES_SERVICE_VEHICLE, false)) {
      this.serviceVehiclesEnabled = true;
    }
    this.subscribeToAutocompletion();
  }

  private subscribeToAutocompletion() {
    this.autocompleteSubscription = this.numberPlateControl
      .valueChanges
      .pipe(
        debounceTime(300),
        switchMap(value => this.truckService.findTrucksByNumberplate(value))
      ).subscribe(result => {
        console.log('subscription', result);
        this.autocomplateTrucks = result
      });
  }

  private unsubscribeFromAutocompletion() {
    this.autocompleteSubscription.unsubscribe();
  }

  ngOnInit() {
    this.numberPlateControl.setValue(this.truck.numberPlate);
  }

  public numberplateAutocompleteSelection(event: any) {
    this.unsubscribeFromAutocompletion();
    try {
      // console.log(event.option.value);
      if (event.option.value != null) {
        this.truck.numberPlate = event.option.value.numberPlate;
        this.truck.carrier = event.option.value.carrier;
        this.truck.driver = event.option.value.driver;
        this.truck.coDriver = event.option.value.coDriver;
        this.truck.supplier = event.option.value.supplier;
        this.truck.useGlobalConnect = event.option.value.useGlobalConnect;
      }
    } finally {
      this.subscribeToAutocompletion();
    }
  }

  public getDisplayFn() {
    return (val) => this.display(val);
  }

  private display(truck: TruckDetails): string {
    if (typeof truck == "string") {
      return truck;
    }
    //access component "this" here
    return truck ? truck.numberPlate : '';
  }

  save(): void {
    this.isLoading = true;
    // Make a copy to save
    const truckToSave = new TruckDetails(this.truck);

    if (this.numberPlateControl.value != null && this.numberPlateControl.value.numberPlate != null) {
      truckToSave.numberPlate = this.numberPlateControl.value.numberPlate;
    } else {
      truckToSave.numberPlate = this.numberPlateControl.value;
    }
    // assign new selected equipment
    this.truckSupplementaryEquipmentComponent.getSelectedSupplementaryEquipment().forEach(e => {
      truckToSave.equipment.push(e);
    });

    if (this.origTruck.serviceTruck && this.origTruck.includedInTruckGroups && truckToSave.serviceTruck === false) {
      this.dialogService.showConfirm('tmc.truck-details-dialog.remove-service-truck.confirm.title', 'tmc.truck-details-dialog.remove-service-truck.confirm.message').afterClosed().subscribe(confirmed => {
        if (confirmed) {
          this.saveTruck(truckToSave);
        } else {
          this.isLoading = false;
        }
      });
    } else {
      this.saveTruck(truckToSave);
    }
  }

  private saveTruck(truckToSave: TruckDetails) {
    this.truckService.saveTruck(truckToSave).subscribe(response => {
      if (response.status === ResponseStatus.SUCCESS) {
        this.dialogRef.close();
      } else {
        this.responseMessages = response.responseMessages;
      }
      this.isLoading = false;
    },
      error => {
        this.isLoading = false;
      }
    );
  }

  isSaveDisabled(): boolean {

    if (!this.truckForm) {
      return true;
    }

    if (this.truckForm.invalid) {
      return true;
    }

    if (this.numberPlateControl.invalid) {
      return true;
    }

    if (this.cps.isRequired('ui-component.truck-details-dialog.number-plate.min-length', true)) {
      if (this.numberPlateControl.value != null && this.numberPlateControl.value.numberPlate != null) {
        if (this.numberPlateControl.value.numberPlate == null || this.numberPlateControl.value.numberPlate.length === 0) {
          return true;
        }
      } else {
        if (this.numberPlateControl.value == null || this.numberPlateControl.value.length === 0) {
          return true;
        }
      }
    }

    return false;
  }

  availableMessagingProvider(): Array<MessagingProvider> {
    return this.tmcContextService.getAvailableMessagingProvider();
  }

  getMessagingProviderLabel(messagingProvider: MessagingProvider): string {
    if (messagingProvider.dictionaryKey) {
      return this.translationService.translate(messagingProvider.label);
    }
    return messagingProvider.label;
  }

}
